import { MapPin, Navigation } from 'lucide-react';
import { Button } from '@/components/ui';
import { parseLatLng } from '@/utils/geo';
import { directionsUrl, mapsPlaceUrl } from '@/utils/directions';
import { trackGetDirections } from '@/lib/analytics';
import { displayName } from '@/seo/text';
// Lazy: Leaflet loads only when this card nears the viewport (it sits below the
// fold in the detail page aside), never with the page chunk itself.
import LazyLeafletMap from '@/components/map/LazyLeafletMap';

export interface LocationMapCardProps {
  /** "lat,lng" string from the API. */
  location?: string | null;
  /** Google Maps place ID — used to open the store's place page (with details). */
  googleMapsPlaceID?: string | null;
  address?: string | null;
  name: string;
  kind?: 'store' | 'shop';
  logoUrl?: string | null;
  /** The store's / shop's id, reported with the GA4 `get_directions` event. */
  entityId?: string | null;
}

export function LocationMapCard({
  location,
  googleMapsPlaceID,
  address,
  name,
  kind = 'store',
  logoUrl,
  entityId,
}: LocationMapCardProps) {
  const coords = location ? parseLatLng(location) : null;
  const placeId = googleMapsPlaceID?.trim();
  // What the button announces and the pin's tooltip shows: the cleaned name
  // (no 𝗯𝗼𝗹𝗱 letters or emoji). The maps links still search the stored name.
  const label = displayName(name);

  // Nothing to show at all.
  if (!coords && !address && !placeId) return null;

  // Prefer the store's Google Maps place ID so the page opens with full details.
  // Rendered as a real link, not a button calling window.open(): it can be
  // middle-clicked, copied and previewed, and screen readers announce it as a
  // link that leaves the site.
  const directionsHref =
    (placeId ? mapsPlaceUrl(placeId, { location, label: name }) : null) ??
    directionsUrl({
      lat: coords?.lat,
      lng: coords?.lng,
      address: address ?? undefined,
      label: name,
    });

  return (
    <div className="rounded-2xl border border-card-border bg-card p-3">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <MapPin size={16} className="text-primary" />
          <span className="text-sm font-bold text-ink">Location</span>
        </div>
        {directionsHref && (
          <Button
            size="sm"
            variant="primary"
            leftIcon={<Navigation size={13} aria-hidden="true" />}
            href={directionsHref}
            target="_blank"
            rel="noopener"
            onClick={() => trackGetDirections({ contentType: kind, itemId: entityId })}
            aria-label={`Directions to ${label} (opens in a new tab)`}
          >
            Directions
          </Button>
        )}
      </div>

      {coords && (
        <div className="h-40 overflow-hidden rounded-xl">
          <LazyLeafletMap
            className="h-full"
            markers={[
              {
                id: 'location',
                kind,
                lat: coords.lat,
                lng: coords.lng,
                title: label,
                logoUrl: logoUrl ?? undefined,
              },
            ]}
            center={coords}
            zoom={15}
            interactive={false}
          />
        </div>
      )}

      {address && <p className="mt-2 line-clamp-2 text-xs text-ink-secondary">{address}</p>}
    </div>
  );
}

export default LocationMapCard;
